import { Button } from "@/components/ui/button";
import { Copy, Upload } from "lucide-react";
import { TVMResults, TVMInputs } from './types';

interface TVMResultsProps {
  results: TVMResults;
  inputs: TVMInputs;
  onCopyToClipboard: (value: number) => void;
  formatCurrency: (amount: number) => string;
  onLoadToInvestment?: (data: any) => void;
}

const TVMResultsDisplay = ({ results, inputs, onCopyToClipboard, formatCurrency, onLoadToInvestment }: TVMResultsProps) => {
  const handleLoadToInvestment = () => {
    if (!onLoadToInvestment) return;
    
    const investmentData = {
      monthly_payment: results.payment || inputs.payment || 0,
      remaining_balance: results.remainingBalance || inputs.presentValue || 0,
      balloon_payment_amount: results.balloonPayment || 0,
      has_balloon_payment: (results.balloonPayment || 0) > 0,
      original_interest_rate: inputs.interestRate || 0,
      term_remaining: inputs.periods ? `${inputs.periods} months` : '',
      asking_price: results.remainingBalance || inputs.presentValue || 0,
      paymentsUntilBalloon: inputs.paymentsUntilBalloon || 0
    };
    
    onLoadToInvestment(investmentData);
  };

  const hasResults = results.payment || results.remainingBalance || results.balloonPayment || results.periods;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Results</h3>
        {hasResults && onLoadToInvestment && (
          <Button
            onClick={handleLoadToInvestment}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
          >
            <Upload className="h-4 w-4 mr-1" />
            Load to Investment
          </Button>
        )}
      </div>
      
      {results.payment && (
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Monthly Payment:</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-blue-600">
                {formatCurrency(results.payment)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopyToClipboard(results.payment!)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {results.periods && (
        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Number of Payments:</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-purple-600">
                {Math.round(results.periods)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopyToClipboard(Math.round(results.periods!))}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {results.balloonPayment !== undefined && results.balloonPayment > 0 && (
        <div className="p-4 bg-orange-50 rounded-lg border-l-4 border-orange-400">
          <div className="flex justify-between items-center">
            <span className="font-medium text-orange-800">Balloon Payment:</span>
            <div className="flex items-center space-x-2">
              <span className="text-xl font-bold text-orange-600">
                {formatCurrency(Math.round(results.balloonPayment * 100) / 100)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopyToClipboard(Math.round(results.balloonPayment! * 100) / 100)}
                className="text-orange-600 hover:text-orange-700 hover:bg-orange-100"
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <p className="text-sm text-orange-700 mt-2">
            Final payment due after {inputs.paymentsUntilBalloon} months
          </p>
        </div>
      )}
      
      {results.remainingBalance !== undefined && (
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Remaining Balance:</span>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-green-600">
                {formatCurrency(results.remainingBalance)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onCopyToClipboard(results.remainingBalance!)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {results.totalPayments && (
        <div className="p-4 bg-yellow-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Total Payments:</span>
            <span className="text-lg font-bold text-yellow-600">
              {formatCurrency(results.totalPayments)}
            </span>
          </div>
        </div>
      )}
      
      {results.totalInterest && (
        <div className="p-4 bg-red-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Total Interest:</span>
            <span className="text-lg font-bold text-red-600">
              {formatCurrency(results.totalInterest)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TVMResultsDisplay;
